// shell.js — adaptives Gerüst (Navigation Bar / Rail / breite Rail) + Top App Bar.
//   const shell = createScaffold(root, { label, destinations, fab, railToggle: true, storageKey, accountInRail })
//   accountInRail: Platz für den Konto-Knopf am Fuß der Rail (Bereiche ohne Kopfzeile, also die App)
//   destinations: [{ id, label, icon, href, secondary?: true (nicht in der Bar), group?: 'Name' (Überschrift davor, breite Rail) }]
//   shell.setActive(id) · shell.setBadge(id, n|true|0) · shell.outlet · shell.topbar(htmlTemplate)
import { html, render, attrs } from '../core/h.js';
import { t } from '../../shared/i18n.js';

export function createScaffold(root, { label = t('ui.nav'), destinations = [], links = [], fab = null, railToggle = true, storageKey = 'vc-rail', accountInRail = false } = {}) {
  let collapsed = false;
  try { collapsed = localStorage.getItem(storageKey) === 'collapsed'; } catch {}
  render(root, html`
    <div class="vc-scaffold" data-rail="${collapsed ? 'collapsed' : 'expanded'}">
      <nav class="vc-nav" aria-label="${label}">
        <div class="vc-nav-top">
          ${railToggle ? html`<button type="button" class="vc-icon-btn vc-rail-toggle" data-rail-toggle aria-label="${t('ui.rail_toggle')}" aria-expanded="${!collapsed}">
            <span class="vc-icon" aria-hidden="true">menu</span></button>` : ''}
          ${fab ? html`<a class="vc-fab is-tertiary vc-nav-fab" href="${fab.href}" aria-label="${fab.label}"><span class="vc-icon" aria-hidden="true">${fab.icon}</span><span class="vc-nav-fab-label">${fab.label}</span></a>` : ''}
        </div>
        ${destinations.map((d) => html`
          ${d.group ? html`<div class="vc-nav-group" role="presentation">${d.group}</div>` : ''}
          <a class="vc-nav-item${d.secondary ? ' is-secondary' : ''}" href="${d.href}" data-dest="${d.id}">
            <span class="vc-nav-ind"><span class="vc-icon" aria-hidden="true">${d.icon}</span></span>
            <span class="vc-nav-label">${d.label}</span>
          </a>`)}
        ${links.length ? html`<div class="vc-nav-links">${links.map((l) => html`
          <a class="vc-nav-item is-secondary is-link" href="${l.href}" ${attrs({ target: l.newTab ? '_blank' : null, rel: l.newTab ? 'noopener' : null, 'aria-label': l.ariaLabel || null, 'data-requires': l.requires || null, hidden: l.requires ? '' : null })}>
            <span class="vc-nav-ind"><span class="vc-icon" aria-hidden="true">${l.icon}</span></span>
            <span class="vc-nav-label">${l.label}</span>
            ${l.newTab ? html`<span class="vc-icon vc-nav-ext" aria-hidden="true">open_in_new</span>` : ''}
          </a>`)}</div>` : ''}
        ${accountInRail ? html`<div class="vc-nav-foot" data-account-slot></div>` : ''}
      </nav>
      <div class="vc-main">
        <header class="vc-topbar" data-topbar></header>
        <div class="vc-outlet" id="outlet" tabindex="-1"></div>
      </div>
    </div>`);
  const scaffold = root.querySelector('.vc-scaffold');
  const outlet = root.querySelector('#outlet');
  const topbarEl = root.querySelector('[data-topbar]');

  scaffold.addEventListener('click', (e) => {
    const t = e.target.closest('[data-rail-toggle]');
    if (!t) return;
    collapsed = scaffold.dataset.rail !== 'collapsed';
    scaffold.dataset.rail = collapsed ? 'collapsed' : 'expanded';
    t.setAttribute('aria-expanded', String(!collapsed));
    try { localStorage.setItem(storageKey, collapsed ? 'collapsed' : 'expanded'); } catch {}
  });

  // Top-App-Bar färbt sich, sobald der Inhalt darunter scrollt
  const onScroll = () => topbarEl.classList.toggle('is-scrolled', window.scrollY > 4);
  addEventListener('scroll', onScroll, { passive: true });

  return {
    root: scaffold, outlet, topbarEl,
    setActive(id) {
      for (const a of scaffold.querySelectorAll('.vc-nav-item')) {
        if (a.dataset.dest === id) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
      }
    },
    setBadge(id, n) {
      const a = scaffold.querySelector(`.vc-nav-item[data-dest="${CSS.escape(id)}"]`);
      if (!a) return;
      let b = a.querySelector('.vc-badge');
      if (!n) { if (b) b.remove(); a.removeAttribute('aria-description'); return; }
      if (!b) { b = document.createElement('span'); b.className = 'vc-badge'; a.querySelector('.vc-nav-ind').appendChild(b); }
      b.textContent = n === true ? '' : (n > 999 ? '999+' : String(n));
      a.setAttribute('aria-description', n === true ? 'neu' : n + ' offen');
    },
    topbar(tpl) { render(topbarEl, tpl); onScroll(); },
  };
}

/** Primäre Tabs mit gleitendem Indikator: tabs(el) nach dem Rendern aufrufen; aktive Tab = aria-selected="true". */
export function initTabs(el) {
  if (!el) return;
  let ind = el.querySelector('.vc-tabs-ind');
  if (!ind) { ind = document.createElement('span'); ind.className = 'vc-tabs-ind'; el.appendChild(ind); }
  const place = () => {
    const a = el.querySelector('[aria-selected="true"]');
    if (!a) { ind.style.width = '0'; return; }
    const label = a.querySelector('.vc-tab-label') || a;
    const w = Math.max(24, label.offsetWidth || a.offsetWidth);
    const left = a.offsetLeft + (a.offsetWidth - w) / 2;
    ind.style.width = w + 'px';
    ind.style.translate = left + 'px 0';
    // Schmale Fenster: aktiven Reiter in den sichtbaren Bereich holen (nur waagerecht — scrollIntoView zöge die Seite mit).
    if (el.scrollWidth > el.clientWidth + 1) {
      const want = a.offsetLeft - (el.clientWidth - a.offsetWidth) / 2;
      el.scrollLeft = Math.max(0, Math.min(want, el.scrollWidth - el.clientWidth));
    }
  };
  place();
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(place).observe(el);
  // Icon- und Textschrift ändern die Tab-Breiten nach dem ersten Messen — danach erneut ausrichten.
  if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) document.fonts.ready.then(place);
  el.addEventListener('keydown', (e) => {
    const tabs = [...el.querySelectorAll('[role="tab"]')];
    const i = tabs.indexOf(document.activeElement);
    if (i < 0) return;
    const n = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null;
    if (n == null) return;
    e.preventDefault();
    const t = tabs[(n + tabs.length) % tabs.length]; t.focus(); t.click();
  });
  return { update: place };
}
